import {AuthorityType} from './authorities';
import {DocumentType} from './documents';

/** Who a payout pays — selects which typed creditor table holds the instruction's creditor row. */
export enum PayoutCreditorKind {
  BENEFICIARY = 'beneficiary',
  AUTHORITY = 'authority'
}

/** Payout instruction status — pure workflow state, no PIS/provider mechanics. */
export enum PayoutInstructionStatus {
  /** Created, awaiting ops review */
  SENT = 'sent',
  /** Ops requires more information before approving */
  CLARIFICATION_REQUESTED = 'clarification_requested',
  /** Ops rejected the request */
  REJECTED = 'rejected',
  /** Ops approved — dispatched or ready to dispatch; finer state lives on `dispatchedAt`, not here */
  APPROVED = 'approved',
  /** Provider accepted (AC*) — debit inflight reserved, bank statement debit not yet confirmed */
  FUNDS_IN_TRANSIT = 'funds_in_transit',
  /** Payment successfully settled — debit confirmed in bank statement */
  COMPLETED = 'completed',
  /** Payment failed at provider, was rejected, or encountered a technical error */
  FAILED = 'failed',
  /** Cancelled before or after authorization */
  CANCELLED = 'cancelled'
}

/** Submitted but not yet approved by ops — still reserves balance, see the reserving set below. */
export const AWAITING_REVIEW_PAYOUT_INSTRUCTION_STATUSES: readonly PayoutInstructionStatus[] = [
  PayoutInstructionStatus.SENT,
  PayoutInstructionStatus.CLARIFICATION_REQUESTED
];

/**
 * Statuses a payout instruction can still be withdrawn from — pre-approval only, since from
 * APPROVED onward the money is already moving at the bank. Shared by the domain entity's
 * canBeCancelled() check, the atomic claim query, and the web action gate so all stay in sync.
 */
export const CANCELLABLE_PAYOUT_INSTRUCTION_STATUSES = AWAITING_REVIEW_PAYOUT_INSTRUCTION_STATUSES;

/**
 * Statuses whose amount still counts against the trust account's available-for-payout balance —
 * i.e. reserved, but not yet reflected in Blnk's own ledger. `FUNDS_IN_TRANSIT` is deliberately
 * excluded: reaching it means the debit inflight was already created in Blnk, so that amount is
 * already subtracted via the ledger's own inflight balance — counting it here too would
 * double-reserve the same money (see `computeAvailablePayoutBalance`).
 */
export const RESERVING_PAYOUT_INSTRUCTION_STATUSES: readonly PayoutInstructionStatus[] = [
  ...AWAITING_REVIEW_PAYOUT_INSTRUCTION_STATUSES,
  PayoutInstructionStatus.APPROVED
];

/**
 * Query hint for narrowing payout instructions to those a bank-statement debit could settle:
 * `APPROVED` is where a MANUAL payout sits permanently (no webhook advances it) and where a
 * dispatched PIS payout waits for its AC* webhook, `FUNDS_IN_TRANSIT` is where a PIS payout waits
 * for the statement.
 *
 * Necessary but not sufficient — `canSettlePayoutInstructionFromStatement` is the actual rule, and
 * `APPROVED` additionally requires `dispatchedAt`. Use this to filter a query, then that to decide.
 */
export const SETTLEABLE_PAYOUT_INSTRUCTION_STATUSES: readonly PayoutInstructionStatus[] = [
  PayoutInstructionStatus.APPROVED,
  PayoutInstructionStatus.FUNDS_IN_TRANSIT
];

/** One review action recorded per row in `payout_instruction_reviews` — supports multiple rounds. */
export enum PayoutReviewAction {
  APPROVED = 'approved',
  REJECTED = 'rejected',
  CLARIFICATION_REQUESTED = 'clarification_requested'
}

/**
 * Closed-list transfer-purpose categories for authority payees. Values come directly from the
 * `payment_reason` enum returned by the payment-demand-document and payment-voucher extractors
 * (Extend AI Studio) — `OTHER` is UI-only and never returned by an extractor.
 */
export enum PayoutPurposeCode {
  MUNICIPAL_TAX = 'municipal_tax',
  SEWER_FEE = 'sewer_fee',
  BETTERMENT_LEVY = 'betterment_levy',
  CAPITALIZATION_FEE = 'capitalization_fee',
  CAPITAL_GAINS_TAX_ADVANCE = 'capital_gains_tax_advance',
  CAPITAL_GAINS_TAX = 'capital_gains_tax',
  PURCHASE_TAX = 'purchase_tax',
  OTHER = 'other'
}

/**
 * Which purpose codes each authority accepts — shared by the web form's dropdown and the domain
 * policy that validates a submitted payout. Tax Authority deliberately has no OTHER: its
 * extractor's enum is closed with no fallback.
 */
export const PAYOUT_PURPOSE_CODES_BY_AUTHORITY: Readonly<
  Record<AuthorityType, readonly PayoutPurposeCode[]>
> = {
  [AuthorityType.MUNICIPALITY]: [
    PayoutPurposeCode.MUNICIPAL_TAX,
    PayoutPurposeCode.SEWER_FEE,
    PayoutPurposeCode.BETTERMENT_LEVY,
    PayoutPurposeCode.OTHER
  ],
  [AuthorityType.ISRAEL_LAND_AUTHORITY]: [
    PayoutPurposeCode.CAPITALIZATION_FEE,
    PayoutPurposeCode.OTHER
  ],
  [AuthorityType.TAX_AUTHORITY]: [
    PayoutPurposeCode.CAPITAL_GAINS_TAX_ADVANCE,
    PayoutPurposeCode.CAPITAL_GAINS_TAX,
    PayoutPurposeCode.PURCHASE_TAX
  ]
} as const;

/**
 * Which document category an authority payout requires — a Tax Authority payout is paid against
 * a voucher, a municipality or the Israel Land Authority against a payment-demand document.
 * Shared by the web app's upload form and ops-web's review page so the classification lives in
 * one place; each app maps the resulting `DocumentType` to its own copy/i18n keys.
 */
export const PAYOUT_CREDITOR_DOCUMENT_TYPE_BY_AUTHORITY = {
  [AuthorityType.TAX_AUTHORITY]: DocumentType.PAYMENT_VOUCHER,
  [AuthorityType.MUNICIPALITY]: DocumentType.PAYMENT_DEMAND_DOCUMENT,
  [AuthorityType.ISRAEL_LAND_AUTHORITY]: DocumentType.PAYMENT_DEMAND_DOCUMENT
} as const satisfies Readonly<Record<AuthorityType, DocumentType>>;
